// src/slices/permission/reducer.ts
// Redux global state cho phân quyền (route/menu + button/input) của user hiện tại — xem mục 9,
// README_SERCURITY.md. Thay cho việc gọi API xác thực mỗi lần đổi route (ModulePermission cũ) và
// static class in-memory tách biệt (PageActionPermission), giờ đọc từ đây để tránh gọi API thừa.
// Sống trong Redux store (RAM) nên tự mất khi đóng tab/trình duyệt — không persist ra localStorage
// (giữ đúng nguyên tắc bảo mật đã áp dụng trước đó: không cho phép F12 sửa quyền phía client).

import { createSlice } from "@reduxjs/toolkit";
import { fetchMyPermissions } from "./thunk";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('permission_messages');

export interface PermissionState {
  isFullAccessPaths: boolean;
  allowedPaths: string[];
  isFullAccessActions: boolean;
  allowedActionCodes: string[];
  // Url trang đầu tiên (theo thứ tự menu) mà role hiện tại được phép xem — dùng làm đích redirect
  // mặc định thay cho việc trỏ cứng "/dashboard" (xem Routes/DefaultRedirect.tsx). null nếu role
  // không có quyền xem trang nào hoặc tải menu thất bại.
  firstAllowedPath: string | null;
  // true sau khi fetchMyPermissions() thành công ít nhất 1 lần — AuthProtected dựa vào cờ này để
  // biết cần gọi lại API hay có thể đọc thẳng state (F5/mở lại trình duyệt sẽ làm mất state vì
  // Redux không persist, nên loaded luôn về false, buộc fetch lại).
  loaded: boolean;
  loading: boolean;
  error: string | null;
  // true khi lần fetch gần nhất bị BE từ chối 401 (token còn hợp lệ theo client nhưng server không
  // chấp nhận) — AuthProtected cần điều hướng về /login thay vì /access-denied.
  sessionExpired: boolean;
  // Timestamp (ms) của lần dispatch fetchMyPermissions() gần nhất — dùng để throttle trong
  // thunk.ts#condition, tránh dồn request khi nhiều API khác nhau trên cùng trang đều trả 403 rải
  // rác theo thời gian (không đồng thời, nên riêng cờ `loading` không chặn hết được).
  lastAttemptAt: number | null;
}

export const initialState: PermissionState = {
  isFullAccessPaths: false,
  allowedPaths: [],
  isFullAccessActions: false,
  allowedActionCodes: [],
  firstAllowedPath: null,
  loaded: false,
  loading: false,
  error: null,
  sessionExpired: false,
  lastAttemptAt: null,
};

const permissionSlice = createSlice({
  name: "Permission",
  initialState,
  reducers: {
    // Dispatch từ AuthProtected khi nhận sự kiện onSessionExpired (api_helper.ts) — 401 từ BẤT KỲ
    // API nào ngoài Login/RefreshToken, khác với sessionExpired do chính fetchMyPermissions() bị từ
    // chối (set qua extraReducers bên dưới). Dùng chung field `sessionExpired` vì AuthProtected đã
    // có sẵn logic redirect "/login" + logout dựa trên field này, không cần thêm state mới.
    setSessionExpired: (state) => {
      state.sessionExpired = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.sessionExpired = false;
        state.lastAttemptAt = Date.now();
      })
      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        state.isFullAccessPaths = action.payload.isFullAccessPaths;
        state.allowedPaths = action.payload.allowedPaths;
        state.isFullAccessActions = action.payload.isFullAccessActions;
        state.allowedActionCodes = action.payload.allowedActionCodes;
        state.firstAllowedPath = action.payload.firstAllowedPath;
        state.loaded = true;
        state.loading = false;
        state.error = null;
        state.sessionExpired = false;
      })
      .addCase(fetchMyPermissions.rejected, (state, action) => {
        const payload = action.payload as
          | { message: string; sessionExpired: boolean }
          | undefined;
        state.loading = false;
        state.error = payload?.message || st('khong_tai_duoc_thong_tin_phan_text');
        state.sessionExpired = !!payload?.sessionExpired;
        // Cố ý KHÔNG set loaded = true khi lỗi — fail-closed: AuthProtected coi như chưa xác thực
        // được quyền (điều hướng /access-denied) thay vì lỡ tay coi allowedPaths rỗng là "loaded".
      });
  },
});

export const { setSessionExpired } = permissionSlice.actions;
export default permissionSlice.reducer;
